import { getTimeBucketLabel, getTimeUnclearLabel } from '../../../utils/timeBucketLabel';
import { getTimelineEntryDate } from '../../../services/timeline/dateUtils';
import { inferTimeBlockFromContext } from '../../../features/careSourceProcessing/parsing';
import { getLogTypeByCategory } from '../../../constants/logTypeRegistry';
import { getCaptureFirstTimelineContext } from './captureFirstTimelineContext';

const TIME_BLOCK_ORDER = ['morning', 'afternoon', 'evening', 'general'];

const TIME_BLOCK_META = {
  morning: {
    label: 'Morning',
    hint: 'Exact and approximate morning entries',
  },
  afternoon: {
    label: 'Afternoon',
    hint: 'Exact and approximate afternoon entries',
  },
  evening: {
    label: 'Evening',
    hint: 'Exact and approximate evening entries',
  },
  general: {
    label: 'No exact time',
    hint: 'Entries saved without a clear time',
  },
};

const parseLocalTimeParts = (value = '') => {
  const match = String(value || '').trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) {
    return null;
  }

  const [, hourText, minuteText = '0', periodText = ''] = match;
  let hours = Number(hourText);
  const minutes = Number(minuteText);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes > 59) {
    return null;
  }

  const period = periodText.toUpperCase();
  if (period === 'PM' && hours < 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  return hours >= 0 && hours <= 23 ? { hours, minutes } : null;
};

const normalizeText = (value) => String(value || '').trim();

const hasMeaningfulClockTime = (date) => (
  date instanceof Date
  && !Number.isNaN(date.getTime())
  && (date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0 || date.getMilliseconds() !== 0)
);

const coerceDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === 'function') {
    const nextDate = value.toDate();
    return Number.isNaN(nextDate.getTime()) ? null : nextDate;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getEntryDateValue = (entry = {}, getEntryDateFn = getTimelineEntryDate) => {
  if (typeof getEntryDateFn === 'function') {
    const result = getEntryDateFn(entry);
    if (result instanceof Date && !Number.isNaN(result.getTime())) {
      return result;
    }
  }

  return coerceDate(entry.timestamp)
    || coerceDate(entry.date)
    || coerceDate(entry.createdAt)
    || coerceDate(entry.entryDate);
};

const getCategoryKey = (entry = {}) => normalizeText(
  getSourceGroupKey(entry)
  || entry.category
  || entry.type
  || entry.incidentCategoryId
  || entry.logCategory
  || 'log'
).toLowerCase();

const getAdaptiveCategoryLabel = (entry = {}) => normalizeText(
  entry.adaptiveQuickLogLabel
  || entry.incidentData?.adaptiveQuickLogLabel
  || entry.incidentCategoryLabel
  || ''
);

const getCategoryLabel = (entry = {}, presentation = {}) => (
  entry.kind === 'source_story' || entry.kind === 'source_receipt'
    ? normalizeText(entry.sourceStoryLabel || 'Source story')
    : getSourceGroupKey(entry)
      ? 'From your note'
      : normalizeText(
        presentation?.categoryLabel
        || presentation?.label
        || getAdaptiveCategoryLabel(entry)
        || getLogTypeByCategory(getCategoryKey(entry))?.displayLabel
        || getLogTypeByCategory(getCategoryKey(entry))?.trackLabel
        || entry.categoryLabel
        || entry.titlePrefix
        || entry.title
        || 'Log'
      )
);

const getSourceGroupContext = (entry = {}) => {
  if (entry.kind === 'story_moment_reference' || entry.pointerKind === 'story_moment_reference') {
    return null;
  }

  return entry.kind === 'source_story' || entry.kind === 'source_receipt'
    ? (entry.sourceStoryContext || null)
    : (getCaptureFirstTimelineContext(entry) || null);
};

const getSourceGroupKey = (entry = {}) => {
  if (entry.kind === 'story_moment_reference' || entry.pointerKind === 'story_moment_reference') {
    return '';
  }

  if (entry.kind === 'source_story' || entry.kind === 'source_receipt') {
    const sourceKey = normalizeText(entry.sourceNoteId || entry.sourceStoryContext?.sourceNoteId || '');
    return sourceKey ? `source-story:${sourceKey}` : '';
  }

  const context = getSourceGroupContext(entry);
  if (!context?.hasVisibleContent) {
    return '';
  }

  const sourceKey = normalizeText(
    context.sourceNotePath
    || context.sourceNoteId
    || entry.metadata?.sourceNotePath
    || entry.metadata?.sourceNoteId
    || entry.source?.sourceNotePath
    || entry.source?.sourceNoteId
    || ''
  );

  if (sourceKey) {
    return `source-note:${sourceKey}`;
  }

  return context.originalNote ? `source-note:raw:${context.originalNote.slice(0, 160)}` : '';
};

const getNarrativeContextText = (entry = {}) => normalizeText(
  entry.body
  || entry.note
  || entry.text
  || entry.content
  || entry.description
  || entry.summary
  || entry.value
  || entry.sourceText
  || entry.originalText
  || entry.title
  || entry.titlePrefix
  || ''
);

const getTimeBlockFromHour = (hour) => {
  if (!Number.isFinite(hour)) {
    return 'general';
  }

  if (hour < 12) {
    return 'morning';
  }

  if (hour < 17) {
    return 'afternoon';
  }

  return 'evening';
};

const getTimeStatus = (entry = {}) => {
  const timeSource = normalizeText(entry.timeSource || entry.metadata?.timeSource || '').toLowerCase();
  const timeBucket = normalizeText(entry.timeBucket || entry.metadata?.timeBucket || '');
  const inferred = Boolean(entry.timeInferred || entry.metadata?.timeInferred || timeBucket || timeSource === 'inferred' || timeSource === 'approximate');
  const contextBlock = inferTimeBlockFromContext(getNarrativeContextText(entry));
  const parsedObservation = Boolean(entry.parsedObservation || entry.metadata?.parsedObservation || entry.source?.parsedObservation || entry.originalData?.parsedObservation);

  if (timeSource === 'missing' || timeSource === 'unknown') {
    return inferred || contextBlock !== 'general' ? 'inferred_block' : 'unknown';
  }

  if (timeSource === 'explicit') {
    return 'exact';
  }

  if (inferred) {
    return 'inferred_block';
  }

  if (contextBlock !== 'general') {
    return 'inferred_block';
  }

  if (parsedObservation) {
    return 'unknown';
  }

  return hasMeaningfulClockTime(getEntryDateValue(entry)) ? 'exact' : 'unknown';
};

const getTimeBlock = (entry = {}, timeStatus = getTimeStatus(entry)) => {
  if (timeStatus === 'exact') {
    const date = getEntryDateValue(entry);
    return date ? getTimeBlockFromHour(date.getHours()) : 'general';
  }

  const timeBucket = normalizeText(entry.timeBucket || entry.metadata?.timeBucket || '').toLowerCase();
  if (timeBucket) {
    if (/(breakfast|before_school|morning|before_leaving_home)/.test(timeBucket)) {
      return 'morning';
    }
    if (/(lunch|after_lunch|nap|afternoon|late_afternoon)/.test(timeBucket)) {
      return 'afternoon';
    }
    if (/(dinner|evening|bedtime|night)/.test(timeBucket)) {
      return 'evening';
    }
  }

  if (timeStatus === 'unknown') {
    return 'general';
  }

  const contextBlock = inferTimeBlockFromContext(getNarrativeContextText(entry));
  if (contextBlock !== 'general') {
    return contextBlock;
  }

  const date = getEntryDateValue(entry);
  if (!date) {
    return 'general';
  }

  return getTimeBlockFromHour(date.getHours());
};

const getReferenceDateLabel = (entry = {}) => {
  const date = getEntryDateValue(entry);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const getNarrativeTimeLabel = (entry = {}, presentation = {}, timeStatus = getTimeStatus(entry), timeBlock = getTimeBlock(entry, timeStatus)) => {
  const date = getEntryDateValue(entry);
  const contextBlock = inferTimeBlockFromContext(getNarrativeContextText(entry));

  if (entry.kind === 'story_moment_reference' || entry.pointerKind === 'story_moment_reference') {
    return getReferenceDateLabel(entry) || getTimeUnclearLabel();
  }

  if (timeStatus === 'exact') {
    const localTime = normalizeText(entry?.localTime || entry?.metadata?.localTime || '');
    const timeParts = parseLocalTimeParts(localTime);
    if (timeParts) {
      const exactTime = new Date(0);
      exactTime.setHours(timeParts.hours, timeParts.minutes, 0, 0);
      return exactTime.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      });
    }

    return date
      ? date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
      : getTimeUnclearLabel();
  }

  if (timeStatus === 'inferred_block') {
    const timeBucket = normalizeText(entry.timeBucket || entry.metadata?.timeBucket || '');
    const bucketLabel = getTimeBucketLabel(timeBucket);
    if (bucketLabel) {
      return `${bucketLabel} note`;
    }

    const sourceLabel = normalizeText(entry.sourceMeta?.senderName || presentation?.sourceMeta?.senderName || '');
    if (sourceLabel) {
      return `${sourceLabel} note`;
    }

    if (timeBlock === 'general') {
      return 'Approximate time';
    }

    const blockLabel = TIME_BLOCK_META[timeBlock]?.label || TIME_BLOCK_META[contextBlock]?.label || 'Approximate';
    return `${blockLabel} note`;
  }

  if (timeStatus === 'unknown') {
    return getTimeUnclearLabel();
  }

  return date
    ? date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
    : getTimeUnclearLabel();
};

const normalizeTimelineNarrativeEntry = (entry = {}, getEntryDateFn = getTimelineEntryDate) => {
  const date = getEntryDateValue(entry, getEntryDateFn);
  const timeStatus = getTimeStatus(entry);
  const timeBlock = getTimeBlock(entry, timeStatus);
  const sortDate = date || new Date(0);

  return {
    ...entry,
    timeStatus,
    timeBlock,
    narrativeTimeLabel: getNarrativeTimeLabel(entry, {}, timeStatus, timeBlock),
    narrativeSortRank: timeStatus === 'exact' ? 0 : timeStatus === 'inferred_block' ? 1 : 2,
    narrativeSortTime: sortDate.getTime(),
  };
};

const compareNarrativeEntries = (a = {}, b = {}) => {
  const rankA = Number(a.narrativeSortRank ?? 2);
  const rankB = Number(b.narrativeSortRank ?? 2);
  if (rankA !== rankB) {
    return rankA - rankB;
  }

  const timeA = Number(a.narrativeSortTime ?? 0);
  const timeB = Number(b.narrativeSortTime ?? 0);
  if (timeA !== timeB) {
    return timeA - timeB;
  }

  return Number(a.__narrativeIndex ?? 0) - Number(b.__narrativeIndex ?? 0);
};

const buildDailyNarrativeBlocks = ({
  entries = [],
  getEntryPresentation = () => ({}),
  getEntryDateFn = getTimelineEntryDate,
}) => {
  const normalizedEntries = entries
    .map((entry, index) => normalizeTimelineNarrativeEntry({ ...entry, __narrativeIndex: index }, getEntryDateFn))
    .sort(compareNarrativeEntries);

  return TIME_BLOCK_ORDER
    .map((timeBlock) => {
      const blockEntries = normalizedEntries.filter((entry) => entry.timeBlock === timeBlock);
      if (!blockEntries.length) {
        return null;
      }

      const categoryMap = new Map();
      blockEntries.forEach((entry, index) => {
        const categoryKey = getCategoryKey(entry);
        const presentation = getEntryPresentation(entry);
        const categoryLabel = getCategoryLabel(entry, presentation);
        const sourceGroupContext = getSourceGroupContext(entry);
        const sourceGroupKey = getSourceGroupKey(entry);
        const isSourceStory = entry.kind === 'source_story' || entry.kind === 'source_receipt';

        if (!categoryMap.has(categoryKey)) {
          categoryMap.set(categoryKey, {
            key: `${timeBlock}-${categoryKey}`,
            categoryKey,
            label: categoryLabel,
            sourceGroup: Boolean(sourceGroupKey),
            sourceStory: isSourceStory,
            sourceGroupKey,
            sourceContext: sourceGroupContext,
            storyItem: isSourceStory ? entry : null,
            entries: [],
            firstIndex: index,
            exactCount: 0,
            inferredCount: 0,
            unknownCount: 0,
          });
        }

        const group = categoryMap.get(categoryKey);
        if (!group.sourceContext && sourceGroupContext) {
          group.sourceContext = sourceGroupContext;
        }
        if (!group.storyItem && isSourceStory) {
          group.storyItem = entry;
        }
        group.entries.push(entry);
        if (entry.timeStatus === 'exact') {
          group.exactCount += 1;
        } else if (entry.timeStatus === 'inferred_block') {
          group.inferredCount += 1;
        } else {
          group.unknownCount += 1;
        }
      });

      const categoryGroups = Array.from(categoryMap.values()).sort((a, b) => (
        a.firstIndex - b.firstIndex
        || a.label.localeCompare(b.label)
      ));

      return {
        key: `time-block-${timeBlock}`,
        period: timeBlock,
        timeBlock,
        label: TIME_BLOCK_META[timeBlock].label,
        hint: TIME_BLOCK_META[timeBlock].hint,
        entries: blockEntries,
        exactCount: blockEntries.filter((entry) => entry.timeStatus === 'exact').length,
        inferredCount: blockEntries.filter((entry) => entry.timeStatus === 'inferred_block').length,
        unknownCount: blockEntries.filter((entry) => entry.timeStatus === 'unknown').length,
        categoryGroups,
      };
    })
    .filter(Boolean);
};

export {
  buildDailyNarrativeBlocks,
  getNarrativeTimeLabel,
  getTimeStatus,
  getTimeBlock,
  normalizeTimelineNarrativeEntry,
};
